import { useEffect, useState } from 'react';
import { InfoIcon } from '@phosphor-icons/react';
import { Button, Modal, Switch, Tooltip } from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';
import StorageProviderService from '@utility/services/storage-provider';

const BLANK_BOX_HELP =
  'By default Blank Box enabled means we will make the inventory available for Walmart fulfillment. Turning this off would disable this inventory from being available at Walmart marketplaces only';

type ItemSettingsModalProps = {
  id: string;
  open: boolean;
  closeModal: () => void;
  name?: string;
  itemId: string;
  blankBoxFulfillment?: boolean;
  refresh: () => void;
};

export function ItemSettingsModal({
  id,
  open,
  closeModal,
  name = '',
  itemId,
  blankBoxFulfillment,
  refresh,
}: ItemSettingsModalProps) {
  const [bbf, setBbf] = useState(!!blankBoxFulfillment);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setBbf(!!blankBoxFulfillment);
      setLoading(false);
    }
  }, [open, blankBoxFulfillment]);

  const handleToggle = async () => {
    setLoading(true);
    try {
      await StorageProviderService.blankBoxFulfillmentSettings({
        id,
        item_id: itemId,
        is_blank_box: !bbf,
      });
      refresh();
      closeModal();

      ToastMessage.success('Blank Box Fulfillment updated');
    } catch (e) {
      ToastMessage.error(e?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      size="md"
      onOpenChange={isOpen => !isOpen && closeModal()}
      isDismissable={!loading}
      title="Settings"
      footer={
        <Button onPress={closeModal} isDisabled={loading}>
          Close
        </Button>
      }
    >
      <div className="sk:flex sk:items-center sk:justify-between sk:py-2.5">
        <span className="sk:text-dense sk:text-fg-secondary">Name</span>
        <span className="sk:text-sm sk:font-medium">{name || '-'}</span>
      </div>

      <div className="sk:flex sk:items-center sk:justify-between sk:py-2.5">
        <span className="sk:flex sk:items-center sk:gap-1 sk:text-dense sk:text-fg-secondary">
          Blank Box Fulfillment
          <Tooltip content={BLANK_BOX_HELP}>
            <InfoIcon size={14} aria-label="About Blank Box Fulfillment" />
          </Tooltip>
        </span>
        <Switch
          isSelected={bbf}
          onChange={handleToggle}
          isDisabled={loading}
          aria-label="Blank Box Fulfillment"
        />
      </div>
    </Modal>
  );
}
